import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileText, Search, X } from 'lucide-react';
import { useI18n } from '../lib/i18n';
import { useDialog } from '../lib/useDialog';

/**
 * LA VENTANA DE PLANTILLAS DEL CORREO (Gabriela, 2026-10-28: "no puede ver sus templates, se ve la mitad
 * del nombre").
 *
 * El selector era un <select> nativo con <optgroup>. Lo que Gabriela veía en Chrome era el desplegable
 * nativo corrido unos 45 px a la izquierda: cada nombre empezaba por la mitad ("ment Authorization Email"
 * por "Payment…"). No era un nombre largo —medido en prod, la etiqueta más larga tiene 39 caracteres—:
 * es cómo Chrome posiciona el popup nativo con zoom de página o pantalla escalada, y eso no se arregla
 * con CSS porque el popup no es parte de la página. La salida es la misma que la de adjuntos: una
 * ventana NUESTRA, en el medio, con los grupos por departamento, el nombre completo, el asunto debajo y
 * un buscador. Los dos composes (claim y comms) la comparten.
 */
export type GrupoDePlantillas = {
  key: string;
  label: string;
  templates: Array<{ id: string; name: string; subject?: string | null }>;
};

export function TemplatePickerModal({ groups, onPick, onClose }: {
  groups: GrupoDePlantillas[];
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const dialogo = useDialog(onClose, { autoFocus: false });
  const [q, setQ] = useState('');
  const visibles = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return groups;
    return groups
      .map((g) => ({ ...g, templates: g.templates.filter((x) => `${x.name} ${x.subject ?? ''} ${g.label}`.toLowerCase().includes(s)) }))
      .filter((g) => g.templates.length > 0);
  }, [groups, q]);
  const total = groups.reduce((n, g) => n + g.templates.length, 0);

  const ui = (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div ref={dialogo.ref} {...dialogo.props} className="card" onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(760px, 96vw)', height: 'min(78vh, 680px)', padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--line)', flexWrap: 'wrap' }}>
          <b style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--fs-md)' }}>
            <FileText size={16} strokeWidth={1.75} /> {t('em.useTemplate')}
          </b>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('tplp.count', { n: String(total) })}</span>
          <span style={{ flex: 1 }} />
          <label style={{ position: 'relative', minWidth: 220 }}>
            <Search size={13} strokeWidth={1.75} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
            <input className="input sm" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('tplp.search')} style={{ paddingLeft: 26, width: '100%' }} />
          </label>
          <button type="button" className="calx-pop-x" onClick={onClose} aria-label={t('common.close')}><X size={14} strokeWidth={1.75} /></button>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '6px 10px 10px' }}>
          {visibles.length === 0 ? (
            <div className="muted" style={{ padding: 16, fontSize: 'var(--fs-sm)' }}>{t('tplp.noMatch')}</div>
          ) : visibles.map((g) => (
            <section key={g.key} style={{ marginTop: 8 }}>
              <div style={{ padding: '6px 8px', position: 'sticky', top: 0, background: 'var(--card)', zIndex: 1, borderBottom: '1px solid var(--line)' }}>
                <b style={{ fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--accent)' }}>{g.label}</b>
                <span className="muted" style={{ fontSize: 'var(--fs-2xs)', marginLeft: 6 }}>{g.templates.length}</span>
              </div>
              {g.templates.map((x) => (
                <button key={x.id} type="button" onClick={() => onPick(x.id)}
                  style={{ width: '100%', textAlign: 'left', display: 'grid', gap: 2, padding: '8px 10px', border: 0, borderRadius: 8, background: 'transparent', cursor: 'pointer', color: 'inherit', font: 'inherit' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-2)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                  <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)' }}>{x.name}</span>
                  {x.subject && <span className="muted" style={{ fontSize: 'var(--fs-2xs)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.subject}</span>}
                </button>
              ))}
            </section>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderTop: '1px solid var(--line)' }}>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('tplp.hint')}</span>
          <span style={{ flex: 1 }} />
          <button type="button" className="btn ghost sm" onClick={onClose}>{t('common.cancel')}</button>
        </div>
      </div>
    </div>
  );
  return createPortal(ui, document.body);
}
